/**
 * SSE protocol parser. Pulled out of <StreamingMarkdown> so it's unit-testable
 * without mounting React or instantiating fetch.
 *
 * The Layer 4 streaming routes emit `event: <type>\ndata: <json>\n\n` frames.
 * This parser maintains a buffer across decoder chunks (TextDecoder slices
 * UTF-8 bytes anywhere) and yields complete events as they arrive.
 */

/**
 * Stateful incremental SSE parser. Returns { events, remainingBuffer }.
 * Caller passes the remaining buffer back on the next chunk.
 */
export function parseSseChunk(buffer) {
  const events = [];
  let remaining = buffer;
  let sepIdx;

  while ((sepIdx = remaining.indexOf("\n\n")) !== -1) {
    const rawEvent = remaining.slice(0, sepIdx);
    remaining = remaining.slice(sepIdx + 2);

    const lines = rawEvent.split("\n");
    const eventType = firstFieldValue(lines, "event:");
    const dataLine = firstFieldValue(lines, "data:");
    if (!eventType || !dataLine) continue;

    let data;
    try { data = JSON.parse(dataLine); }
    catch { continue; }

    events.push({ type: eventType, data });
  }

  return { events, remainingBuffer: remaining };
}

function firstFieldValue(lines, prefix) {
  for (const l of lines) {
    if (l.startsWith(prefix)) return l.slice(prefix.length).trim();
  }
  return null;
}

/**
 * Reduce a stream of events into accumulated state. Used by the test set
 * to validate StreamingMarkdown's behavior from a sequence of events.
 */
export function reduceStreamingEvents(events, initial = { accumulated: "", final: null, error: null }) {
  let state = { ...initial };
  for (const e of events) {
    if (e.type === "delta") {
      state.accumulated = (state.accumulated || "") + (e.data?.text || "");
    } else if (e.type === "done") {
      state.final = e.data;
    } else if (e.type === "error") {
      state.error = e.data?.message || "stream error";
    }
  }
  return state;
}
